<template>
  <span v-if="services.length <= 1" class="font-medium">
    {{ services[0]?.title ?? "-" }}
  </span>

  <span v-else>
    <!-- Plain ``<button>`` instead of PrimeVue ``<Button link>`` so the text
         keeps the same font-weight, padding, and line-height as the
         single-service cell — otherwise the row's vertical alignment drifts
         when one row has the button and adjacent rows have plain text. -->
    <button
      type="button"
      class="font-medium text-primary cursor-pointer hover:underline bg-transparent border-0 p-0 leading-inherit align-baseline"
      @click.stop="togglePopover"
    >
      {{ services.length }} Services
    </button>
    <Popover ref="popoverRef">
      <ul class="m-0 pl-4 list-disc space-y-1 text-sm text-gray-700">
        <li v-for="service in services" :key="service.id">
          {{ service.title }}
        </li>
      </ul>
    </Popover>
  </span>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import Popover from "primevue/popover";
import type { BenefitServiceDisplaySummary } from "../helpers";

// Module-level registry of every ``BenefitServicesCell`` popover mounted in the
// app. The trigger button uses ``@click.stop`` so PrimeVue's own click-outside
// listener can't see a click on a sibling row's trigger; without this
// coordination, MAI-4560 reproduces — opening a second cell's popover doesn't
// close the first and they overlap.
//
// We unconditionally call ``hide()`` on every other registered popover before
// toggling the current one. ``hide()`` is a safe no-op when the popover is
// already closed, so the registry works even if PrimeVue's own click-outside
// handler closed something behind our back (e.g. user clicked the page body).
//
// Pinned to globalThis so the registry survives any HMR module re-evaluation
// and so every cell mounted in the app coordinates through the same Set.
const REGISTRY_KEY = "__benefitServicesCellPopoverRegistry";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const registry: Set<InstanceType<typeof Popover>> =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ((globalThis as any)[REGISTRY_KEY] ??= new Set());

const props = defineProps<{
  serviceSummaries?: readonly BenefitServiceDisplaySummary[];
}>();

const popoverRef = ref<InstanceType<typeof Popover> | null>(null);

const services = computed(() => props.serviceSummaries ?? []);

function togglePopover(event: Event) {
  const current = popoverRef.value;
  if (!current) return;

  registry.forEach((other) => {
    if (other !== current) other.hide();
  });
  current.toggle(event);
}

onMounted(() => {
  if (popoverRef.value) registry.add(popoverRef.value);
});

onBeforeUnmount(() => {
  if (popoverRef.value) registry.delete(popoverRef.value);
});
</script>
